import React, { useState, useCallback } from 'react';
import styles from '../ReportCard.module.scss';
import moment from 'moment';
import MiniMap from '../MiniMap';
import Pagination from './Pagination';
import SearchBar from './SearchBar';
import { isFarFromKaraKeche } from '../utils/geo';
import { formatDuration } from '../utils/formatDuration';
import { getDriverDocuments } from 'service/DriverService';

/**
 * Таблица текущих перевозок (in-route).
 */
const InRouteTableView = ({
  inRouteLoads,
  inRouteCount,
  inRouteNext,
  inRoutePrev,
  inRoutePage,
  setInRoutePage,
  inRouteSearch,
  setInRouteSearch,
  nowTime,
  user,
}) => {
  const [visibleMaps, setVisibleMaps] = useState({});
  const [showLegend, setShowLegend] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailMaps, setDetailMaps] = useState({});
  const [driverDocs, setDriverDocs] = useState({});
  const [docPreview, setDocPreview] = useState(null);

  const toggleMap = (id, geoType) => {
    const key = `${id}-${geoType}`;
    setVisibleMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDetailMap = (mapKey) => {
    setDetailMaps(prev => ({ ...prev, [mapKey]: !prev[mapKey] }));
  };

  const fetchDocumentsIfNeeded = useCallback(async (item) => {
    if (!user?.is_admin_of && !user?.is_report_of) return;
    const driverId = item.driver_id || item.driver;
    if (!driverId) return;
    const itemId = item.id;
    if (driverDocs[itemId]) return;
    setDriverDocs(prev => ({ ...prev, [itemId]: { loading: true, error: null, docs: [] } }));
    try {
      const res = await getDriverDocuments(driverId);
      setDriverDocs(prev => ({ ...prev, [itemId]: { loading: false, error: null, docs: res.data } }));
    } catch (e) {
      setDriverDocs(prev => ({ ...prev, [itemId]: { loading: false, error: 'Ошибка загрузки', docs: [] } }));
    }
  }, [user, driverDocs]);

  const handleRowClick = (item) => {
    const id = item.id;
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
      setDetailMaps({});
      fetchDocumentsIfNeeded(item);
    }
  };

  const renderDetailCard = (item) => {
    const baseName = item.top_base_name || item.chpp_name || item.private_point_name || '—';
    const canSeeDocs = user?.is_admin_of || user?.is_report_of;
    const docsState = driverDocs[item.id];

    let timeAExit = null;
    if (item.time_a_exit) {
      timeAExit = moment(item.time_a_exit, 'DD.MM.YYYY HH:mm', true);
      if (!timeAExit.isValid()) timeAExit = moment(item.time_a_exit);
    }
    const transitDuration = timeAExit && timeAExit.isValid() ? formatDuration(timeAExit, nowTime) : '—';

    const GeoField = ({ geo, mapKey }) => {
      if (!geo) return null;
      return (
        <div>
          <div className={styles.detailGeoRow}>
            <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${geo}`, '_blank')}>📍 Открыть на карте</button>
            <button className={styles.detailGeoBtn} onClick={() => toggleDetailMap(mapKey)}>{detailMaps[mapKey] ? '✕ Скрыть карту' : '🗺️ Мини-карта'}</button>
          </div>
          {detailMaps[mapKey] && <div className={styles.detailMapWrap}><MiniMap geo={geo} /></div>}
        </div>
      );
    };

    const InfoItem = ({ label, value }) => (
      <div className={styles.detailInfoItem}>
        <span className={styles.detailInfoLabel}>{label}</span>
        <div className={styles.detailInfoValue}>{value}</div>
      </div>
    );

    const PhoneLink = ({ phone }) => {
      if (!phone || phone === '-') return <span>—</span>;
      return <a href={`tel:${phone}`} className={styles.detailPhoneLink}>📞 {phone}</a>;
    };

    return (
      <div className={styles.detailCard}>
        <div className={styles.detailCardTopBar}>
          <div className={styles.detailCardTitle}>
            <span>🚚</span> Подробности поездки
          </div>
          <div className={styles.detailDurationBadges}>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeLoading}`}>
              <span>⛏️ Погрузка</span>
              <span>{item.duration_str_loading || '—'}</span>
            </div>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeDelivery}`}>
              <span>🚚 В пути</span>
              <span>{transitDuration}</span>
            </div>
          </div>
        </div>

        <div className={styles.detailBody}>
          {item.notes && (
            <div className={styles.detailNotes}>
              <strong>Примечания:</strong> {item.notes}
            </div>
          )}
          {/* Водитель */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>👤 Водитель</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="ФИО" value={item.employee_name || '—'} />
              <InfoItem label="Телефон" value={<PhoneLink phone={item.employee_phone} />} />
              <InfoItem label="Гос. номер" value={item.vehicle_number || '—'} />
              <InfoItem label="Модель" value={item.vehicle_model || '—'} />
            </div>
            {canSeeDocs && (() => {
              const DOC_LABELS = ['Фото ТС','Паспорт (л)','Паспорт (о)','Права (л)','Права (о)','Тех.пасп. (л)','Тех.пасп. (о)'];
              const byDesc = {};
              if (docsState?.docs) docsState.docs.forEach(d => { if (d.description) byDesc[d.description] = d; });
              const otherDocs = docsState?.docs?.filter(d => !DOC_LABELS.includes(d.description)) || [];
              return (
                <div className={styles.detailDocsWrap}>
                  <div className={styles.detailDocsTitle}>📎 Документы водителя</div>
                  {!docsState || docsState.loading ? (
                    <div className={styles.detailDocsLoading}>Загрузка документов…</div>
                  ) : docsState.error ? (
                    <div className={styles.detailDocsError}>{docsState.error}</div>
                  ) : (
                    <>
                      <div className={styles.detailDocsGrid}>
                        {DOC_LABELS.map(label => {
                          const doc = byDesc[label];
                          const fileUrl = doc ? (doc.file_url || doc.file) : null;
                          const isActive = docPreview?.url === fileUrl && fileUrl;
                          return (
                            <button key={label}
                              className={`${styles.detailDocBtn} ${doc ? styles.detailDocBtnHas : styles.detailDocBtnMissing} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => { if (!fileUrl) return; setDocPreview(isActive ? null : { url: fileUrl, label }); }}
                              title={doc ? `Открыть: ${label}` : `${label} — не загружено`}
                            >
                              <span className={styles.detailDocBtnIcon}>{doc ? '' : '—'}</span>
                              <span className={styles.detailDocBtnLabel}>{label}</span>
                            </button>
                          );
                        })}
                        {otherDocs.map(doc => {
                          const fileUrl = doc.file_url || doc.file;
                          const isActive = docPreview?.url === fileUrl;
                          return (
                            <button key={doc.id}
                              className={`${styles.detailDocBtn} ${styles.detailDocBtnHas} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => setDocPreview(isActive ? null : { url: fileUrl, label: doc.description || doc.file_name || 'Файл' })}
                            >
                              <span className={styles.detailDocBtnIcon}>📁</span>
                              <span className={styles.detailDocBtnLabel}>{doc.description || doc.file_name || 'Файл'}</span>
                            </button>
                          );
                        })}
                        {!docsState?.docs?.length && (
                          <div className={styles.detailDocsEmpty} style={{ gridColumn: '1 / -1' }}>Документы не загружены</div>
                        )}
                      </div>
                      {docPreview && (
                        <div className={styles.detailDocPreview}>
                          <div className={styles.detailDocPreviewHeader}>
                            <div className={styles.detailDocPreviewTitle}>
                              <span className={styles.detailDocPreviewIcon}>📄</span>
                              <span>{docPreview.label}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>↗️ Открыть</a>
                              <button className={styles.detailDocPreviewClose} onClick={() => setDocPreview(null)}>✕</button>
                            </div>
                          </div>
                          <div className={styles.detailDocPreviewBody}>
                            <img src={docPreview.url} alt={docPreview.label} className={styles.detailDocPreviewImg}
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                            />
                            <div style={{ display: 'none', padding: '24px', textAlign: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>⇩ Скачать файл</a>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* База */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>🏭 База назначения</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="Название" value={<span style={{ color: '#0d6efd', fontWeight: 700 }}>{baseName}</span>} />
              <InfoItem label="Телефон" value={<PhoneLink phone={item.top_base_data?.phone} />} />
              {(item.top_base_name ? 'Топливная база' : item.chpp_name ? 'ТЭЦ' : item.private_point_name ? 'Соц. объект / МСУ' : null) && (
                <InfoItem label="Тип" value={item.top_base_name ? 'Топливная база' : item.chpp_name ? 'ТЭЦ' : 'Соц. объект / МСУ'} />
              )}
            </div>
            {Array.isArray(item.geo_base) && item.geo_base.length > 0 && (
              <div className={styles.detailSubSection}>
                <div className={styles.detailSubSectionTitle}>📍 Точки базы</div>
                {item.geo_base.map((g, i) => (
                  <div key={g.id ?? i} className={styles.detailBranchRow}>
                    <span className={styles.detailBranchName}>{g.name}</span>
                    {g.address && <span className={styles.detailBranchAddr}>{g.address}</span>}
                    {g.geo && (
                      <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${g.geo}`, '_blank')}>📍 Открыть</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Въезд */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>🚛 Въезд в Кара-Кече</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="Время въезда" value={item.time_a || '—'} />
              <InfoItem label="СБ въезда" value={item.guard_name || '—'} />
              <InfoItem label="Телефон СБ" value={<PhoneLink phone={item.guard_phone} />} />
            </div>
            {item.geo_a && (
              <div className={styles.detailGeoSection}>
                <span className={styles.detailGeoLabel}>Геолокация въезда</span>
                <GeoField geo={item.geo_a} mapKey={`detail-${item.id}-geo_a`} />
              </div>
            )}
          </div>

          {/* Выезд */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>🏁 Выезд с Кара-Кече</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="Время выезда" value={item.time_a_exit || '—'} />
              <InfoItem label="СБ выезда" value={item.exit_guard_name || '—'} />
              <InfoItem label="Телефон СБ" value={<PhoneLink phone={item.exit_guard_phone} />} />
            </div>
            {item.geo_a_exit && (
              <div className={styles.detailGeoSection}>
                <span className={styles.detailGeoLabel}>Геолокация выезда</span>
                <GeoField geo={item.geo_a_exit} mapKey={`detail-${item.id}-geo_a_exit`} />
              </div>
            )}
          </div>

        </div>
      </div>
    );
  };

  return (
    <div className={styles.table_info_wrapper}>
      {showLegend && (
        <div className={styles.legendOverlay} onClick={() => setShowLegend(false)}>
          <div className={styles.legendModal} onClick={e => e.stopPropagation()}>
            <div className={styles.legendHeader}>
              <span>🎨 Значение цветов строк</span>
              <button className={styles.legendClose} onClick={() => setShowLegend(false)}>✕</button>
            </div>
            <div className={styles.legendBody}>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#fff', border: '1px solid #dee2e6' }} />
                <div>
                  <strong>Обычный фон</strong>
                  <div>Время в пути менее 1 суток или указано примечание</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#FFC107' }} />
                <div>
                  <strong>Жёлтый</strong>
                  <div>Время в пути более 1 суток (без примечания)</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#fd7e14' }} />
                <div>
                  <strong>Оранжевый</strong>
                  <div>Время в пути более 2 суток (без примечания)</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#f8d7da' }} />
                <div>
                  <strong>Красный</strong>
                  <div>Время в пути более 3 суток (без примечания)</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#fff', borderLeft: '4px solid #dc3545' }} />
                <div>
                  <strong>Красная полоса слева</strong>
                  <div>Машина далеко от Кара-Кече (аномальная геолокация)</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#fff', borderLeft: '4px solid #ffc107' }} />
                <div>
                  <strong>Жёлтая полоса слева</strong>
                  <div>Время с момента въезда более 60 часов</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Pagination
        page={inRoutePage}
        setPage={setInRoutePage}
        count={inRouteCount}
        hasNext={!!inRouteNext}
        hasPrev={!!inRoutePrev}
        shownCount={inRouteLoads.length}
        onLegend={() => setShowLegend(true)}
      />

      <div className={styles.completedSearchRow}>
        <SearchBar
          value={inRouteSearch}
          onChange={setInRouteSearch}
          onClear={() => setInRouteSearch('')}
          placeholder="Поиск по водителю, телефону, Базе, статусу, номеру машины, модели машины..."
        />
        <span className={styles.completedBadge} style={{ background: '#e3f2fd', color: '#1565c0', borderColor: '#90caf9' }}>🚚 В пути</span>
      </div>

      <div className={styles.tableScrollWrap}>
        <table className={`${styles.dataTable} ${styles.inRouteTable}`}>
          <thead>
            <tr>
              <th>#</th>
              <th>Машина</th>
              <th>Водитель</th>
              <th>База назначения</th>
              <th>Въезд (Кара-Кече)</th>
              <th>Выезд (Кара-Кече)</th>
              <th>Время в пути</th>
              <th>Примечания</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(inRouteLoads) ? inRouteLoads : []).map((item, index) => {
              const start = item.time_a ? moment(item.time_a, 'DD.MM.YYYY HH:mm') : null;
              const diff = start ? moment.duration(nowTime.diff(start)) : null;
              const hours = diff ? Math.floor(diff.asHours()) : 0;
              const isFar = isFarFromKaraKeche(item.geo_a);
              const sequentialNumber = (inRoutePage - 1) * 10 + index + 1;

              const statusText = item.status === 'Въехал в точку А'
                ? 'Въехал в Кара-Кече'
                : item.status === 'Въехал в точку Б'
                  ? 'Въехал в Базу'
                  : item.status;

              const hasNotes = item.notes && item.notes.trim();
              let rowBg = {};
              if (!hasNotes && start && start.isValid()) {
                const hoursInRoute = nowTime.diff(start, 'hours');
                if (hoursInRoute >= 72) {
                  rowBg = { background: '#f8d7da' };
                } else if (hoursInRoute >= 48) {
                  rowBg = { background: '#fd7e14', color: '#fff' };
                } else if (hoursInRoute >= 24) {
                  rowBg = { background: '#FFC107' };
                }
              }
              const borderStyle = isFar
                ? { borderLeft: '4px solid #dc3545' }
                : hours > 60 ? { borderLeft: '4px solid #ffc107' } : {};
              const rowStyle = { ...rowBg, ...borderStyle };

              const mapKeyA = `${item.id}-geo_a`;
              const mapKeyAExit = `${item.id}-geo_a_exit`;

              return (
                <React.Fragment key={item.id}>
                  <tr
                    style={{ ...rowStyle, cursor: 'pointer', userSelect: 'none', background: expandedRowId === item.id ? '#eef4ff' : rowStyle.background }}
                    onDoubleClick={() => handleRowClick(item)}
                    onClick={() => { if (window.innerWidth <= 640) handleRowClick(item); }}
                    title="Двойной клик — подробная карточка"
                  >
                    <td>{sequentialNumber}</td>
                    <td data-label="Машина">
                      <div style={{ fontWeight: '700' }}>{item.vehicle_number || '-'}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>{item.vehicle_model || '-'}</div>
                    </td>
                    <td data-label="Водитель">
                      <div>👤 {item.employee_name || 'Не указан'}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>{item.employee_phone || '-'}</div>
                    </td>
                    <td data-label="База" style={{ color: '#0d6efd', fontWeight: '600' }}>
                      {item.top_base_name || item.chpp_name || item.private_point_name || '-'}
                    </td>
                    <td data-label="Въезд КК">
                      <div>{item.time_a || '-'}</div>
                      {item.geo_a && (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                          <button onClick={() => window.open(`https://www.google.com/maps?q=${item.geo_a}`, '_blank')} className={styles.geoLinkBtn}>📍</button>
                          <button onClick={() => toggleMap(item.id, 'geo_a')} className={styles.mapToggleBtn}>{visibleMaps[mapKeyA] ? '✕' : '🗺️'}</button>
                        </div>
                      )}
                    </td>
                    <td data-label="Выезд КК">
                      <div>{item.time_a_exit || '-'}</div>
                      {item.geo_a_exit && (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                          <button onClick={() => window.open(`https://www.google.com/maps?q=${item.geo_a_exit}`, '_blank')} className={styles.geoLinkBtn}>📍</button>
                          <button onClick={() => toggleMap(item.id, 'geo_a_exit')} className={styles.mapToggleBtn}>{visibleMaps[mapKeyAExit] ? '✕' : '🗺️'}</button>
                        </div>
                      )}
                    </td>
                    <td data-label="В пути" style={{ fontWeight: '700', color: hours > 60 ? '#dc3545' : '#198754' }}>
                      {item.time_a_exit ? formatDuration(item.time_a_exit, nowTime) : '-'}
                    </td>
                    <td data-label="Примечания" style={{ fontSize: '12px' }}>{item.notes || '-'}</td>
                  </tr>
                  {item.geo_a && visibleMaps[mapKeyA] && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0 }}>
                        <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                          <MiniMap geo={item.geo_a} />
                        </div>
                      </td>
                    </tr>
                  )}
                  {item.geo_a_exit && visibleMaps[mapKeyAExit] && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0 }}>
                        <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                          <MiniMap geo={item.geo_a_exit} />
                        </div>
                      </td>
                    </tr>
                  )}
                  {expandedRowId === item.id && (
                    <tr className={styles.detailCardRow}>
                      <td colSpan={8}>
                        {renderDetailCard(item)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={inRoutePage}
        setPage={setInRoutePage}
        count={inRouteCount}
        hasNext={!!inRouteNext}
        hasPrev={!!inRoutePrev}
        shownCount={inRouteLoads.length}
        style={{ marginTop: '8px', marginBottom: 0 }}
      />
    </div>
  );
};

export default React.memo(InRouteTableView);
